import React, { useState } from "react";
import {
  Stack,
  Card,
  Text,
  Divider,
  Modal,
  Textarea,
  Button,
  Group,
} from "@mantine/core";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";

// The scale-related legal document text shown in a rep's agreement. Each entry
// in the overview's document_text_types is tagged with a type and carries the
// document_text used wherever that scale type is referenced.
const DOCUMENT_TEXT_TYPES = [
  { value: "COMMISSION_SCALE", label: "Commission Scale" },
  { value: "MD_SCALE", label: "Md Scale" },
  { value: "OVERRIDE_SCALE", label: "Override Scale" },
  { value: "CUSTOM_OVERRIDE", label: "Custom User Override" },
  { value: "CUSTOM_MD_OVERRIDE", label: "Custom MD Override" },
  { value: "SPLIT_MD", label: "Split MD" },
];

function DocumentTextTypesList({ documentTextTypes, onTypesChange }) {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();

  const [editingType, setEditingType] = useState(null);
  const [legalText, setLegalText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const items = Array.isArray(documentTextTypes) ? documentTextTypes : [];

  const findText = value => {
    const entry = items.find(item => item?.type === value);
    return entry?.document_text ?? "";
  };

  const handleOpen = type => {
    setEditingType(type);
    setLegalText(findText(type.value));
  };

  const handleClose = () => {
    setEditingType(null);
  };

  const handleSubmit = async () => {
    if (!editingType) return;

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();

      if (!jwt) {
        throw new Error("Authentication failed");
      }

      const response = await fetch(
        getApiEndpoint("/v1/ps-company-types/document-text"),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
          body: JSON.stringify({
            type: editingType.value,
            document_text: legalText.trim(),
          }),
        }
      );

      if (response.ok) {
        // The save endpoint returns the updated list, so use it directly rather
        // than refetching the whole overview.
        const result = await response.json().catch(() => null);
        const updatedList =
          result?.document_text_types ??
          (Array.isArray(result?.data) ? result.data : null) ??
          (Array.isArray(result) ? result : null);
        if (updatedList && onTypesChange) {
          onTypesChange(updatedList);
        }
        showSuccess("Legal document text updated successfully!");
        handleClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to update legal document text"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to update legal document text");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          The following legal document text is used wherever each scale type is
          referenced in a rep&apos;s agreement.
        </Text>
        <Stack gap="sm">
          {DOCUMENT_TEXT_TYPES.map(type => {
            const documentText = findText(type.value);
            return (
              <Card
                key={type.value}
                padding="md"
                radius="md"
                withBorder
                onClick={() => handleOpen(type)}
                style={{ cursor: "pointer" }}
              >
                <Stack gap="xs">
                  <Text fw={600}>{type.label}</Text>
                  <Divider />
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                      Legal Document Text
                    </Text>
                    <Text size="sm" c="dimmed">
                      {documentText || "No document text set."}
                    </Text>
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </Stack>

      <Modal
        opened={!!editingType}
        onClose={handleClose}
        title={editingType?.label}
        size="lg"
      >
        <Stack gap="md">
          <Textarea
            label="Legal Document Text"
            placeholder="Enter the legal document text"
            value={legalText}
            onChange={e => setLegalText(e.target.value)}
            minRows={4}
            autosize
          />
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} loading={submitting}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default DocumentTextTypesList;
